import { Button, Label, Modal, Radio, Rating, Textarea } from 'flowbite-react';
import moment from 'moment';
import React, { useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { useMutation, useQueryClient } from 'react-query';
import StarRatings from 'react-star-ratings';
import { camelCaseToSpace } from 'shared-utils';
import { submitParticipantFeedback } from 'src/apis/session';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { GuestUserDocument, ParticipantFeedback, UIUser, UserDocument } from 'types';

interface Props {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	mentor?: UserDocument;
	levelUpParticipant?: UserDocument;
	participant?: GuestUserDocument;
	sessionId?: string;

	isMentorSession?: boolean;
}
const FeedbackModal = ({
	isMentorSession,
	isOpen,
	participant,
	setIsOpen,
	mentor,
	levelUpParticipant,
	sessionId,
}: Props) => {
	const [rating, setRating] = React.useState(0);
	const toast = useToast();
	const queryClient = useQueryClient();
	const { isAlumniConnectLoggedIn, isMentorLoggedIn, isJobseekerLoggedIn } = useUserContext();
	const submitParticipantFeedbackMutation = useMutation(['submitParticipantFeedback'], submitParticipantFeedback, {
		onSuccess() {
			toast({
				title: 'Feedback submitted successfully',
				type: 'success',
			});
			if (isMentorLoggedIn || isAlumniConnectLoggedIn) {
				queryClient.refetchQueries(['getAllSessions']);
			} else if (isJobseekerLoggedIn) {
				queryClient.refetchQueries(['getAllBookedParticipantSessions']);
			}

			setIsOpen(false);
		},
		onError(error: Error) {
			setIsOpen(false);
			toast({
				title: `Error submitting feedback: ${error.message}`,
				type: 'error',
			});
		},
	});
	const [feedbacks, setFeedbacks] = React.useState<ParticipantFeedback[]>([]);
	const feedbackQuestions = isMentorSession
		? isMentorLoggedIn
			? [
					{
						id: 1,
						questionText: `How did you find the mentee's technical knowledge and skills?*`,
						type: 'text',
						isOptional: false,
					},
					{
						id: 2,
						questionText: `Did the mentee seem engaged and receptive to feedback?*`,
						type: 'radio',
						options: ['Yes', 'No'],
						isOptional: false,
					},
					{
						id: 3,
						questionText: 'Are there any areas where you think the mentee could improve?',
						type: 'text',
						isOptional: true,
					},
			  ]
			: [
					{
						id: 1,
						questionText: 'Did the mentor provide valuable insights and guidance? *',
						type: 'radio',
						options: ['Yes', 'No'],
						isOptional: false,
					},
					{
						id: 2,
						questionText: 'How would you rate your experience with your mentor? *',
						type: 'rating',
						isOptional: false,
					},
					{ id: 3, questionText: 'Share your feedback about the mentor.', type: 'text', isOptional: true },
			  ]
		: [
				{
					id: 1,
					questionText: 'Please define techinal capability of the participant ? *',
					type: 'radio',
					options: ['Beginner', 'Moderate', 'Advance'],
					isOptional: false,
				},
				{
					id: 2,
					questionText: 'Does the participant behaved properly in the session ? *',
					type: 'radio',
					options: ['Yes', 'No'],
					isOptional: false,
				},
				{
					id: 3,
					questionText: 'How would you rate the Communications skill of the participant (out of 5) ? *',
					type: 'radio',
					options: ['1', '2', '3', '4', '5'],
					isOptional: false,
				},
				{
					id: 4,
					questionText: 'How did find the intent of the participant to join Masai School ? *',
					type: 'radio',
					options: ['High', 'Medium', 'Low'],
					isOptional: false,
				},
				{
					id: 5,
					questionText: 'Please rate the participant overall based on the session ? *',
					type: 'rating',
					isOptional: false,
				},
				{
					id: 6,
					questionText: 'Any other feedback, you would like to share ?',
					type: 'text',
					isOptional: true,
				},
		  ];
	const [isFeedbackFormFilled, setIsFeedbackFormFilled] = React.useState(false);

	const renderFeedbackQuestion = (question: any) => {
		switch (question.type) {
			case 'radio':
				return (
					<div>
						<p className="mt-[24px] text-[14px] font-[500] leading-[21px] dark:text-[#FAFAFC] font-inter">
							{question.questionText}
						</p>

						<fieldset
							className="mt-[8px] flex flex-wrap max-w-md gap-4"
							onChange={e => {
								const target = e.target as HTMLInputElement;
								const feedbackQuestion = feedbacks.find(
									feedback => feedback.feedbackQuestion === question.questionText,
								);
								if (feedbackQuestion) {
									const updatedFeedbacks = feedbacks.map(feedback => {
										if (feedback.feedbackQuestion === question.questionText) {
											return {
												...feedback,
												feedback: target.value,
											};
										} else {
											return feedback;
										}
									});
									setFeedbacks(updatedFeedbacks);
								} else {
									setFeedbacks([
										...feedbacks,
										{
											feedbackQuestion: question.questionText,
											feedback: target.value,
										},
									]);
								}
							}}
						>
							{question.options?.map((option: string, i: number) => (
								<div key={i} className="flex items-center gap-2">
									<Radio
										className="text-[#7777E7] focus:ring-[#7777E7]"
										id={option}
										name={`feedbackRadioQuestion-${question.id}`}
										value={option}
										checked={
											feedbacks.find(feedback => feedback.feedbackQuestion === question.questionText)?.feedback ===
											option
										}
									/>
									<Label htmlFor={option}>{option}</Label>
								</div>
							))}
						</fieldset>
					</div>
				);
			case 'rating':
				return (
					<div className="flex flex-col">
						<p className="text-[14px] font-[500] leading-[21px] dark:text-[#FAFAFC] font-inter">
							{question.questionText}
						</p>
						<div className="mx-auto mt-[16px]">
							<StarRatings
								rating={rating}
								starRatedColor="#e3a008"
								starHoverColor="#e3a008"
								changeRating={(rating: number) => {
									setRating(rating);
									const feedbackQuestion = feedbacks.find(
										feedback => feedback.feedbackQuestion === question.questionText,
									);
									if (feedbackQuestion) {
										const updatedFeedbacks = feedbacks.map(feedback => {
											if (feedback.feedbackQuestion === question.questionText) {
												return {
													...feedback,
													rating,
												};
											} else {
												return feedback;
											}
										});
										setFeedbacks(updatedFeedbacks);
									} else {
										setFeedbacks([
											...feedbacks,
											{
												feedbackQuestion: question.questionText,
												rating,
											},
										]);
									}
								}}
								numberOfStars={5}
								name="rating"
							/>
						</div>
					</div>
				);
			case 'text':
				return (
					<div>
						<p className="mt-[24px] text-[14px] font-[500] leading-[21px] dark:text-[#FAFAFC] font-inter">
							{question.questionText}
						</p>
						<div className="mx-auto mt-[16px]">
							<Textarea
								onChange={e => {
									const target = e.target as HTMLTextAreaElement;
									const feedbackQuestion = feedbacks.find(
										feedback => feedback.feedbackQuestion === question.questionText,
									);
									if (feedbackQuestion) {
										const updatedFeedbacks = feedbacks.map(feedback => {
											if (feedback.feedbackQuestion === question.questionText) {
												return {
													...feedback,
													feedback: target.value,
												};
											} else {
												return feedback;
											}
										});
										setFeedbacks(updatedFeedbacks);
									} else {
										setFeedbacks([
											...feedbacks,
											{
												feedbackQuestion: question.questionText,
												feedback: target.value,
											},
										]);
									}
								}}
								className="h-[120px]"
								placeholder="Overall my interview was great experience."
							/>
						</div>
					</div>
				);
		}
	};

	return (
		<Modal
			show={isOpen}
			size="2xl"
			onClose={() => {
				setIsOpen(false);
			}}
			position="center"
		>
			<Modal.Body className="max-h-[85vh] overflow-y-scroll bg-[#F7F9FB] dark:bg-[#1F2A37] rounded-[8px]">
				<div>
					<div className="">
						<h6 className="mb-[24px] text-[20px] font-[700] leading-[24px] dark:text-[#CCDDFF] font-inter">
							Feedback for session with{' '}
							{isMentorSession ? (isMentorLoggedIn ? levelUpParticipant?.name : mentor?.name) : participant?.name}
						</h6>
						<div className="grid gap-[16px]">
							{feedbackQuestions.map((question, i) => {
								return <React.Fragment key={i}>{renderFeedbackQuestion(question)}</React.Fragment>;
							})}
						</div>

						<Button
							disabled={submitParticipantFeedbackMutation.isLoading}
							onClick={() => {
								sessionId &&
									submitParticipantFeedbackMutation.mutate({
										sessionId: sessionId,
										feedbacks,
										isHostFeedback: isMentorLoggedIn || isAlumniConnectLoggedIn,
									});
							}}
							color="purplePlaced"
							className="mt-[32px] w-fit flex ml-auto"
						>
							Submit
						</Button>
					</div>
				</div>
			</Modal.Body>

			<RxCross2
				onClick={() => {
					setIsOpen(false);
				}}
				className={`absolute right-[16px] top-[16px] h-[20px] w-[20px] cursor-pointer text-[#9CA3AF]`}
			/>
		</Modal>
	);
};

export default FeedbackModal;
